import jsPDF from "jspdf";

/**
 * Generate a 1-page professional executive briefing PDF.
 * Designed for top officials (Menteri / Gubernur / Kepala Lembaga).
 * No html2canvas — pure vector for crisp, fast output and small file size.
 */
export async function exportDashboardPDF(_target, summary = {}) {
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const W = pdf.internal.pageSize.getWidth();    // ~595
  const H = pdf.internal.pageSize.getHeight();   // ~842
  const M = 40; // margin
  const now = summary.generatedAt || new Date();

  // ===== BACKGROUND =====
  pdf.setFillColor(248, 250, 252); // slate-50
  pdf.rect(0, 0, W, H, "F");

  // ===== HEADER BAND =====
  pdf.setFillColor(5, 14, 26); // nms bg
  pdf.rect(0, 0, W, 96, "F");

  // Cyan accent line
  pdf.setFillColor(34, 211, 238);
  pdf.rect(0, 96, W, 2, "F");

  // Logo hexagon (drawn vector)
  const cx = M + 18, cy = 48, r = 16;
  pdf.setDrawColor(34, 211, 238);
  pdf.setFillColor(15, 34, 54);
  pdf.setLineWidth(1.4);
  const hex = [];
  for (let i = 0; i < 6; i++) {
    const ang = -Math.PI / 2 + (Math.PI / 3) * i;
    hex.push([cx + r * Math.cos(ang), cy + r * Math.sin(ang)]);
  }
  pdf.lines(
    hex.map((p, i) => [p[0] - hex[i === 0 ? hex.length - 1 : i - 1][0], p[1] - hex[i === 0 ? hex.length - 1 : i - 1][1]]),
    hex[5][0], hex[5][1], [1, 1], "FD", true
  );
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(34, 211, 238);
  pdf.text("N", cx - 4, cy + 4);

  // Header title
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(14);
  pdf.setFont("helvetica", "bold");
  pdf.text("NATIONAL & MONITORING SYSTEM", M + 46, 42);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(34, 211, 238);
  pdf.text("REPUBLIK INDONESIA · BRIEFING EKSEKUTIF", M + 46, 58);

  // Right side: classification + date
  pdf.setFontSize(8);
  pdf.setTextColor(148, 163, 184);
  pdf.text("KLASIFIKASI", W - M, 32, { align: "right" });
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(250, 204, 21);
  pdf.text("INTERNAL · TERBATAS", W - M, 46, { align: "right" });
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(203, 213, 225);
  const dateLine = now.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const timeLine = now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) + " WIB";
  pdf.text(dateLine, W - M, 64, { align: "right" });
  pdf.text(timeLine, W - M, 76, { align: "right" });

  // ===== STATUS HERO =====
  let y = 130;
  pdf.setTextColor(15, 23, 42);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("STATUS NASIONAL", M, y);
  pdf.setDrawColor(34, 211, 238);
  pdf.setLineWidth(1);
  pdf.line(M, y + 4, M + 90, y + 4);

  y += 22;
  const status = (summary.nationalStatus || "AMAN").toUpperCase();
  const statusColor =
    status === "KRITIS" ? [239, 68, 68] :
    status === "WASPADA" ? [249, 115, 22] :
    status === "SIAGA" ? [250, 204, 21] : [16, 185, 129];

  // Status big card
  pdf.setFillColor(255, 255, 255);
  pdf.setDrawColor(226, 232, 240);
  pdf.setLineWidth(0.8);
  pdf.roundedRect(M, y, W - 2 * M, 64, 8, 8, "FD");

  // Left: status label
  pdf.setFillColor(...statusColor);
  pdf.rect(M, y, 6, 64, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(28);
  pdf.setTextColor(...statusColor);
  pdf.text(status, M + 22, y + 38);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(100, 116, 139);
  pdf.text("Indeks risiko agregat nasional", M + 22, y + 54);

  // Right: score + delta
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(36);
  pdf.setTextColor(15, 23, 42);
  const score = summary.nationalScore != null ? String(summary.nationalScore) : "—";
  pdf.text(score, W - M - 20, y + 40, { align: "right" });
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(100, 116, 139);
  pdf.text("SKOR / 100", W - M - 20, y + 54, { align: "right" });

  // ===== KEY METRICS GRID (2x2) =====
  y += 78;
  pdf.setTextColor(15, 23, 42);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("INDIKATOR KUNCI", M, y);
  pdf.setDrawColor(34, 211, 238);
  pdf.line(M, y + 4, M + 90, y + 4);
  y += 14;

  const cardW = (W - 2 * M - 12) / 2;
  const cardH = 56;
  const metrics = [
    { label: "Provinsi Waspada/Kritis", value: `${(summary.waspadaCount || 0) + (summary.kritisCount || 0)}`, sub: "dari 38 provinsi", color: [249, 115, 22] },
    { label: "Alert Aktif", value: `${(summary.alerts || []).length}`, sub: "membutuhkan tindak lanjut", color: [239, 68, 68] },
    { label: "Isu Termonitor", value: `${summary.totalIssues ?? "—"}`, sub: "dalam 24 jam terakhir", color: [34, 211, 238] },
    { label: "Sentimen Negatif", value: `${(summary.negSentimentPct ?? 0).toFixed ? summary.negSentimentPct.toFixed(0) : summary.negSentimentPct || 0}%`, sub: "dari total pemberitaan", color: [167, 139, 250] },
  ];
  metrics.forEach((m, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = M + col * (cardW + 12);
    const yy = y + row * (cardH + 8);
    pdf.setFillColor(255, 255, 255);
    pdf.setDrawColor(226, 232, 240);
    pdf.roundedRect(x, yy, cardW, cardH, 6, 6, "FD");
    pdf.setFillColor(...m.color);
    pdf.rect(x, yy, 3, cardH, "F");
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    pdf.setTextColor(100, 116, 139);
    pdf.text(m.label.toUpperCase(), x + 12, yy + 16);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(22);
    pdf.setTextColor(15, 23, 42);
    pdf.text(m.value, x + 12, yy + 40);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(100, 116, 139);
    pdf.text(m.sub, x + 12, yy + 52);
  });

  y += 2 * (cardH + 8) + 14;

  // ===== ALERT PRIORITAS =====
  pdf.setTextColor(15, 23, 42);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("ALERT PRIORITAS", M, y);
  pdf.setDrawColor(239, 68, 68);
  pdf.line(M, y + 4, M + 90, y + 4);
  y += 14;

  const alerts = (summary.alerts || []).slice(0, 5);
  if (alerts.length === 0) {
    pdf.setFillColor(240, 253, 244);
    pdf.setDrawColor(187, 247, 208);
    pdf.roundedRect(M, y, W - 2 * M, 32, 6, 6, "FD");
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(22, 101, 52);
    pdf.text("Tidak ada alert kritis. Sistem dalam kondisi terkendali.", M + 14, y + 20);
    y += 40;
  } else {
    alerts.forEach((a) => {
      const sevColor = a.severity === "critical" ? [239, 68, 68] : [249, 115, 22];
      const sevLabel = a.severity === "critical" ? "KRITIS" : "WASPADA";
      pdf.setFillColor(255, 255, 255);
      pdf.setDrawColor(226, 232, 240);
      pdf.roundedRect(M, y, W - 2 * M, 30, 5, 5, "FD");
      // Severity tag
      pdf.setFillColor(...sevColor);
      pdf.roundedRect(M + 8, y + 8, 48, 14, 3, 3, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7.5);
      pdf.setTextColor(255, 255, 255);
      pdf.text(sevLabel, M + 32, y + 17, { align: "center" });
      // Title + msg
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.setTextColor(15, 23, 42);
      pdf.text(pdf.splitTextToSize(a.title, W - 2 * M - 80)[0], M + 66, y + 14);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(100, 116, 139);
      const msg = pdf.splitTextToSize(a.message || "", W - 2 * M - 80)[0] || "";
      pdf.text(msg, M + 66, y + 25);
      y += 34;
    });
  }

  // ===== AI EXECUTIVE INSIGHT =====
  y += 8;
  pdf.setTextColor(15, 23, 42);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("RINGKASAN EKSEKUTIF AI", M, y);
  pdf.setDrawColor(167, 139, 250);
  pdf.line(M, y + 4, M + 130, y + 4);
  y += 14;

  pdf.setFillColor(245, 243, 255);
  pdf.setDrawColor(196, 181, 253);
  const insightH = 100;
  pdf.roundedRect(M, y, W - 2 * M, insightH, 6, 6, "FD");
  pdf.setFillColor(167, 139, 250);
  pdf.rect(M, y, 3, insightH, "F");

  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(9.5);
  pdf.setTextColor(60, 60, 90);
  const insight = summary.aiInsight || buildFallbackInsight(summary);
  const insightLines = pdf.splitTextToSize(insight, W - 2 * M - 26);
  pdf.text(insightLines.slice(0, 7), M + 16, y + 18);

  y += insightH + 10;

  // ===== TOP RISK PROVINCES (compact table) =====
  pdf.setTextColor(15, 23, 42);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("PROVINSI BERISIKO TERTINGGI", M, y);
  pdf.setDrawColor(34, 211, 238);
  pdf.line(M, y + 4, M + 150, y + 4);
  y += 14;

  const topProvs = (summary.topRiskProvinces || []).slice(0, 5);
  if (topProvs.length) {
    // Table header
    pdf.setFillColor(15, 34, 54);
    pdf.rect(M, y, W - 2 * M, 18, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.setTextColor(34, 211, 238);
    pdf.text("RANK", M + 10, y + 12);
    pdf.text("PROVINSI", M + 50, y + 12);
    pdf.text("STATUS", W - M - 110, y + 12);
    pdf.text("SKOR", W - M - 14, y + 12, { align: "right" });
    y += 18;
    topProvs.forEach((p, i) => {
      const bg = i % 2 === 0 ? [255, 255, 255] : [248, 250, 252];
      pdf.setFillColor(...bg);
      pdf.rect(M, y, W - 2 * M, 18, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(100, 116, 139);
      pdf.text(`#${i + 1}`, M + 10, y + 12);
      pdf.setTextColor(15, 23, 42);
      pdf.text((p.name || "").toUpperCase(), M + 50, y + 12);
      const stColor =
        (p.status || "").toLowerCase() === "kritis" ? [239, 68, 68] :
        (p.status || "").toLowerCase() === "waspada" ? [249, 115, 22] :
        (p.status || "").toLowerCase() === "siaga" ? [202, 138, 4] : [16, 185, 129];
      pdf.setTextColor(...stColor);
      pdf.text((p.status || "—").toUpperCase(), W - M - 110, y + 12);
      pdf.setTextColor(15, 23, 42);
      pdf.text(String(p.score ?? "—"), W - M - 14, y + 12, { align: "right" });
      y += 18;
    });
  }

  // ===== FOOTER =====
  pdf.setDrawColor(226, 232, 240);
  pdf.line(M, H - 50, W - M, H - 50);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7.5);
  pdf.setTextColor(100, 116, 139);
  pdf.text(
    "Dokumen di-generate otomatis oleh National & Monitoring System · Bersifat rahasia untuk pejabat berwenang.",
    M, H - 34
  );
  pdf.text(`Halaman 1 dari 1 · ${dateLine} · ${timeLine}`, W - M, H - 34, { align: "right" });

  // Page indicator pill bottom-center
  pdf.setFillColor(34, 211, 238);
  pdf.roundedRect(W / 2 - 30, H - 22, 60, 12, 6, 6, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  pdf.setTextColor(2, 20, 31);
  pdf.text("BRIEFING NMS", W / 2, H - 13, { align: "center" });

  const fname = `Briefing_NMS_${now.toISOString().slice(0, 10)}_${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}.pdf`;
  pdf.save(fname);
  return fname;
}

function buildFallbackInsight(s) {
  const ws = (s.waspadaCount || 0) + (s.kritisCount || 0);
  const status = (s.nationalStatus || "AMAN").toUpperCase();
  const topNames = (s.topRiskProvinces || []).slice(0, 3).map((p) => p.name).join(", ");
  return [
    `Pada periode pelaporan ini, status risiko nasional berada pada level ${status} dengan skor agregat ${s.nationalScore ?? "—"}.`,
    `Terdapat ${ws} provinsi yang memerlukan perhatian khusus${topNames ? ` — di antaranya ${topNames}` : ""}.`,
    `Sebanyak ${(s.alerts || []).length} alert aktif terdeteksi dari pemantauan multi-domain (risiko wilayah, kamtibmas, harga pangan, dan sentimen media).`,
    `Sistem merekomendasikan koordinasi lintas-sektor pada wilayah berisiko tertinggi dan pemantauan ketat terhadap fluktuasi indikator strategis dalam 24-48 jam ke depan.`,
  ].join(" ");
}
